import React, { useState, useEffect } from 'react';
import {
  Users,
  BookOpen,
  Activity,
  Plus,
  Trash2,
  AlertCircle,
  Sparkles,
  Check,
  ChevronDown,
  ChevronRight,
  History,
  Play,
  RotateCcw,
  Clock,
  Copy,
  Edit3,
  Eye,
  Save,
  X,
} from 'lucide-react';
import {
  getTerminology,
  saveTerminology,
  deleteTerminology,
  getUsers,
  getLogs,
  updateUserRole,
  deleteUser,
  TerminologyItem,
  AuditLogEntry,
  SystemPrompt,
  getAllActivePrompts,
  getPromptsForAgent,
  createPromptVersion,
  activatePrompt,
} from '../services/store';
import { User, LANGUAGE_LABELS, LanguageCode } from '../types';
import { supabase } from '../services/supabaseClient';

// Agent display names and descriptions
const AGENT_INFO: Record<string, { name: string; description: string; color: string }> = {
  journalist: {
    name: 'Journalist Agent',
    description: 'Polishes content, fixes grammar, applies editorial feedback',
    color: 'blue',
  },
  critic: {
    name: 'Critic Agent',
    description: 'Reviews content and identifies issues (ai_fixable vs human_required)',
    color: 'orange',
  },
  critic_translation: {
    name: 'Translation Reviewer',
    description: 'Proofreads translated content for grammar and accuracy',
    color: 'purple',
  },
  social: {
    name: 'Social Media Agent',
    description: 'Generates social media posts for various platforms',
    color: 'pink',
  },
  translator: {
    name: 'Translator Agent',
    description: 'Translates content between Dari, Pashto, and English',
    color: 'green',
  },
};

const Admin: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'users' | 'logs' | 'terms' | 'prompts'>('users');
  const [users, setUsers] = useState<User[]>([]);
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [terms, setTerms] = useState<TerminologyItem[]>([]);
  const [refresh, setRefresh] = useState(0);

  // Terminology Form State
  const [showTermForm, setShowTermForm] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [newTerm, setNewTerm] = useState<Partial<TerminologyItem>>({
    language: 'fa-AF',
    type: 'forbidden'
  });

  // Prompt Management State
  const [activePrompts, setActivePrompts] = useState<SystemPrompt[]>([]);
  const [expandedAgent, setExpandedAgent] = useState<string | null>(null);
  const [agentVersions, setAgentVersions] = useState<Record<string, SystemPrompt[]>>({});
  const [loadingVersions, setLoadingVersions] = useState<string | null>(null);
  const [editingPrompt, setEditingPrompt] = useState<string | null>(null);
  const [editPromptText, setEditPromptText] = useState('');
  const [editPromptNotes, setEditPromptNotes] = useState('');
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [promptSuccess, setPromptSuccess] = useState<string | null>(null);
  const [viewingPrompt, setViewingPrompt] = useState<SystemPrompt | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (activeTab === 'prompts') {
        const prompts = await getAllActivePrompts();
        setActivePrompts(prompts);
      } else {
        const [u, l, t] = await Promise.all([getUsers(), getLogs(), getTerminology()]);
        setUsers(u);
        setLogs(l);
        // Convert terminology to legacy format
        setTerms(t.map((term: any) => ({
          id: term.id,
          original: term.term_en || '',
          replacement: term.term_dari || term.term_pashto || '',
          language: term.term_dari ? 'fa-AF' : 'ps-AF',
          type: 'standard' as const,
          context: term.notes,
          createdBy: term.created_by,
          createdAt: term.created_at,
        })));
      }
    };
    fetchData();
  }, [refresh, activeTab]);

  // Load version history for an agent
  const loadAgentVersions = async (agentName: string) => {
    if (expandedAgent === agentName) {
      setExpandedAgent(null);
      return;
    }

    setLoadingVersions(agentName);
    const versions = await getPromptsForAgent(agentName);
    setAgentVersions(prev => ({ ...prev, [agentName]: versions }));
    setExpandedAgent(agentName);
    setLoadingVersions(null);
  };

  // Start editing a prompt (create new version)
  const startEditingPrompt = (prompt: SystemPrompt) => {
    setEditingPrompt(prompt.agent_name);
    setEditPromptText(prompt.prompt_text);
    setEditPromptNotes('');
    setPromptSuccess(null);
  };

  // Save new prompt version
  const savePromptVersion = async (activate: boolean = false) => {
    if (!editingPrompt || !editPromptText.trim()) return;

    setSavingPrompt(true);
    setPromptSuccess(null);

    try {
      const newId = await createPromptVersion(
        editingPrompt,
        editPromptText,
        editPromptNotes || undefined,
        activate
      );

      if (newId) {
        setPromptSuccess(activate ? 'New version created and activated!' : 'New version saved as draft.');
        setEditingPrompt(null);
        setEditPromptText('');
        setEditPromptNotes('');

        // Refresh data
        const prompts = await getAllActivePrompts();
        setActivePrompts(prompts);

        // Reload versions if expanded
        if (expandedAgent) {
          const versions = await getPromptsForAgent(expandedAgent);
          setAgentVersions(prev => ({ ...prev, [expandedAgent]: versions }));
        }
      }
    } catch (error) {
      console.error('Error saving prompt:', error);
    } finally {
      setSavingPrompt(false);
    }
  };

  // Activate a specific version
  const handleActivatePrompt = async (promptId: string, agentName: string) => {
    if (!confirm('Activate this version? The current active version will be deactivated.')) return;

    const success = await activatePrompt(promptId);
    if (success) {
      setPromptSuccess('Prompt version activated!');

      // Refresh data
      const prompts = await getAllActivePrompts();
      setActivePrompts(prompts);

      // Reload versions
      const versions = await getPromptsForAgent(agentName);
      setAgentVersions(prev => ({ ...prev, [agentName]: versions }));
    }
  };

  // --- User Management Handlers ---

  const handleRoleChange = async (userId: string, userName: string, newRole: string) => {
    if (confirm(`Are you sure you want to change ${userName}'s role to ${newRole}?`)) {
      try {
        await updateUserRole(userId, newRole);
        setRefresh(prev => prev + 1);
      } catch (e: any) {
        alert("Failed to update role: " + e.message);
      }
    }
  };

  const handleDeleteUser = async (userId: string, userName: string) => {
    if (confirm(`Are you sure you want to suspend/delete ${userName}? They will no longer be able to access the system.`)) {
      try {
        await deleteUser(userId);
        setRefresh(prev => prev + 1);
      } catch (e: any) {
        alert("Failed to suspend user: " + e.message);
      }
    }
  };

  // --- Terminology Handlers ---

  const handleSaveTerm = async () => {
    setFormError(null);
    if (!newTerm.original || !newTerm.replacement || !newTerm.language) {
      setFormError("Original term, Replacement term, and Language are required.");
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Must be logged in to save terms.");

      await saveTerminology({
        id: crypto.randomUUID(),
        original: newTerm.original,
        replacement: newTerm.replacement,
        language: newTerm.language,
        type: newTerm.type as 'forbidden' | 'standard',
        context: newTerm.context,
      }, user.id);

      setRefresh(prev => prev + 1);
      setShowTermForm(false);
      setNewTerm({ language: 'fa-AF', type: 'forbidden' });
    } catch (e: any) {
      setFormError(e.message);
    }
  };

  const handleDeleteTerm = async (id: string) => {
    if (confirm("Delete this terminology rule?")) {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await deleteTerminology(id, user.id);
        setRefresh(prev => prev + 1);
      }
    }
  };

  const getUserName = (userId?: string) => {
    if (!userId) return 'System';
    const user = users.find(u => u.id === userId);
    return user ? (user.full_name || user.email || 'Unknown') : 'Unknown User';
  };

  const getAgentColor = (agentName: string) => {
    return AGENT_INFO[agentName]?.color || 'slate';
  };

  return (
    <div className="p-8 max-w-7xl mx-auto h-full overflow-y-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Admin Dashboard</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">Manage users, view activity, configure terminology, and manage AI prompts.</p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-xl mb-6 w-fit">
        <button
          onClick={() => setActiveTab('users')}
          className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'users' ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900'}`}
        >
          <Users className="h-4 w-4 mr-2" /> Users
        </button>
        <button
          onClick={() => setActiveTab('logs')}
          className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'logs' ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900'}`}
        >
          <Activity className="h-4 w-4 mr-2" /> Activity Logs
        </button>
        <button
          onClick={() => setActiveTab('terms')}
          className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'terms' ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900'}`}
        >
          <BookOpen className="h-4 w-4 mr-2" /> Terminology DB
        </button>
        <button
          onClick={() => setActiveTab('prompts')}
          className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'prompts' ? 'bg-white dark:bg-slate-700 text-purple-600 dark:text-purple-400 shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-900'}`}
        >
          <Sparkles className="h-4 w-4 mr-2" /> AI Prompts
        </button>
      </div>

      {/* Success Message */}
      {promptSuccess && (
        <div className="mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 p-3 rounded-lg flex items-center justify-between">
          <div className="flex items-center">
            <Check className="h-4 w-4 mr-2" />
            {promptSuccess}
          </div>
          <button onClick={() => setPromptSuccess(null)} className="text-green-500 hover:text-green-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden min-h-[400px]">
        {/* USERS TAB */}
        {activeTab === 'users' && (
          <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
            <thead className="bg-slate-50 dark:bg-slate-800/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">User</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Role</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Desk</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {users.map(user => {
                const isSuspended = user.role === 'suspended';
                return (
                  <tr key={user.id} className={isSuspended ? "bg-slate-50 dark:bg-slate-900/50 opacity-60" : ""}>
                    <td className="px-6 py-4 flex items-center">
                      <img
                        src={user.avatar_url || `https://ui-avatars.com/api/?name=${user.full_name || user.email}`}
                        className="h-8 w-8 rounded-full mr-3"
                        alt=""
                      />
                      <span className={`text-sm font-medium ${isSuspended ? 'text-slate-500 line-through' : 'text-slate-900 dark:text-slate-100'}`}>
                        {user.full_name || user.email}
                      </span>
                      {isSuspended && <span className="ml-2 text-xs text-red-500 font-bold">(Suspended)</span>}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-500">
                       <select
                          value={user.role || 'journalist'}
                          onChange={(e) => handleRoleChange(user.id, user.full_name || user.email || '', e.target.value)}
                          className={`bg-slate-100 dark:bg-slate-800 border-none rounded text-xs uppercase font-bold tracking-wider py-1 px-2 cursor-pointer focus:ring-2 focus:ring-blue-500 ${
                            isSuspended ? 'text-red-600' : 'text-slate-700 dark:text-slate-300'
                          }`}
                        >
                          <option value="journalist">Journalist</option>
                          <option value="editor">Editor</option>
                          <option value="senior_editor">Senior Editor</option>
                          <option value="producer">Producer</option>
                          <option value="supervisor">Supervisor</option>
                          <option value="admin">Admin</option>
                          <option value="suspended">Suspended</option>
                        </select>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-500">{user.email}</td>
                    <td className="px-6 py-4 text-sm text-slate-500 uppercase">{user.desk || '-'}</td>
                    <td className="px-6 py-4 text-right">
                       {!isSuspended && (
                         <button
                           onClick={() => handleDeleteUser(user.id, user.full_name || user.email || '')}
                           className="p-2 text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors"
                           title="Suspend User"
                         >
                           <Trash2 className="h-4 w-4" />
                         </button>
                       )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* LOGS TAB */}
        {activeTab === 'logs' && (
          <div className="flex flex-col">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
                <thead className="bg-slate-50 dark:bg-slate-800/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Time</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">User</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Action</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Entity</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                  {logs.map(log => (
                    <tr key={log.id}>
                      <td className="px-6 py-3 text-sm text-slate-500 whitespace-nowrap">
                        {new Date(log.created_at).toLocaleString()}
                      </td>
                      <td className="px-6 py-3 text-sm font-medium text-slate-900 dark:text-slate-100">
                        {log.user?.full_name || log.user?.email || 'System'}
                      </td>
                      <td className="px-6 py-3 text-sm text-blue-600 dark:text-blue-400">{log.action}</td>
                      <td className="px-6 py-3 text-sm text-slate-500 truncate max-w-md">
                        {log.entity_type ? `${log.entity_type}/${log.entity_id?.slice(0, 8)}` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TERMINOLOGY TAB */}
        {activeTab === 'terms' && (
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-900/30 flex-1 mr-6">
                <h3 className="font-semibold text-blue-900 dark:text-blue-100 text-sm">How this works</h3>
                <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                  Terms defined here are strictly enforced by the AI. Duplicate terms for the same language are prevented.
                </p>
              </div>
              <button
                onClick={() => {
                  setFormError(null);
                  setShowTermForm(true);
                }}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm font-medium"
              >
                <Plus className="h-4 w-4 mr-2" /> Add Term
              </button>
            </div>

            {showTermForm && (
              <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-lg border border-slate-200 dark:border-slate-700 mb-6 animate-in fade-in slide-in-from-top-4">
                {formError && (
                  <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 p-3 rounded-md flex items-center text-sm">
                    <AlertCircle className="h-4 w-4 mr-2" /> {formError}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Incorrect / Original Term</label>
                    <input
                      type="text"
                      value={newTerm.original || ''}
                      onChange={e => setNewTerm({...newTerm, original: e.target.value})}
                      className="w-full rounded-md border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm"
                      placeholder="e.g. Daneshgah"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Correct Replacement</label>
                    <input
                      type="text"
                      value={newTerm.replacement || ''}
                      onChange={e => setNewTerm({...newTerm, replacement: e.target.value})}
                      className="w-full rounded-md border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm"
                      placeholder="e.g. Pohantoon"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Language</label>
                    <select
                       value={newTerm.language}
                       onChange={e => setNewTerm({...newTerm, language: e.target.value})}
                       className="w-full rounded-md border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm"
                    >
                      {Object.entries(LANGUAGE_LABELS).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Context / Notes</label>
                    <input
                      type="text"
                      value={newTerm.context || ''}
                      onChange={e => setNewTerm({...newTerm, context: e.target.value})}
                      className="w-full rounded-md border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm"
                      placeholder="e.g. Use Afghan terminology for University"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3">
                  <button onClick={() => setShowTermForm(false)} className="text-sm text-slate-500 hover:text-slate-900 px-3 py-2">Cancel</button>
                  <button onClick={handleSaveTerm} className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700">Save Rule</button>
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
                <thead className="bg-slate-50 dark:bg-slate-800/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Original</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Replacement</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Language</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Created By</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Context</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                  {terms.map(term => (
                    <tr key={term.id} className="group hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="px-6 py-4 text-sm text-red-500 line-through decoration-red-500/50">{term.original}</td>
                      <td className="px-6 py-4 text-sm font-bold text-green-600 dark:text-green-400">{term.replacement}</td>
                      <td className="px-6 py-4 text-sm text-slate-500">{LANGUAGE_LABELS[term.language as LanguageCode] || term.language}</td>
                      <td className="px-6 py-4 text-sm text-slate-500">
                        <div className="font-medium text-slate-700 dark:text-slate-300">{getUserName(term.createdBy)}</div>
                        <div className="text-xs text-slate-400">{term.createdAt ? new Date(term.createdAt).toLocaleDateString() : '-'}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-500 italic">{term.context}</td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleDeleteTerm(term.id)}
                          className="text-slate-300 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {terms.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-6 py-10 text-center text-slate-400 italic">No terminology rules defined yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* PROMPTS TAB */}
        {activeTab === 'prompts' && (
          <div className="p-6">
            {/* Info Banner */}
            <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-100 dark:border-purple-900/30 mb-6">
              <h3 className="font-semibold text-purple-900 dark:text-purple-100 text-sm flex items-center">
                <Sparkles className="h-4 w-4 mr-2" /> AI Prompt Management
              </h3>
              <p className="text-xs text-purple-700 dark:text-purple-300 mt-1">
                Manage and version AI agent prompts. Changes take effect immediately when activated. Previous versions are preserved for rollback.
              </p>
            </div>

            {/* Editing Panel */}
            {editingPrompt && (
              <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-lg border border-slate-200 dark:border-slate-700 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-slate-900 dark:text-white flex items-center">
                    <Edit3 className="h-4 w-4 mr-2" />
                    Editing: {AGENT_INFO[editingPrompt]?.name || editingPrompt}
                  </h4>
                  <button
                    onClick={() => setEditingPrompt(null)}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="mb-4">
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Prompt Text
                  </label>
                  <textarea
                    value={editPromptText}
                    onChange={(e) => setEditPromptText(e.target.value)}
                    rows={15}
                    className="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm font-mono text-slate-900 dark:text-white"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Change Notes (optional)
                  </label>
                  <input
                    type="text"
                    value={editPromptNotes}
                    onChange={(e) => setEditPromptNotes(e.target.value)}
                    placeholder="e.g., Improved categorization for ai_fixable issues"
                    className="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm"
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setEditingPrompt(null)}
                    className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => savePromptVersion(false)}
                    disabled={savingPrompt}
                    className="px-4 py-2 text-sm bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-300 dark:hover:bg-slate-600 disabled:opacity-50"
                  >
                    <Save className="h-4 w-4 inline mr-1" />
                    Save as Draft
                  </button>
                  <button
                    onClick={() => savePromptVersion(true)}
                    disabled={savingPrompt}
                    className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                  >
                    <Play className="h-4 w-4 inline mr-1" />
                    Save & Activate
                  </button>
                </div>
              </div>
            )}

            {/* Agent List */}
            <div className="space-y-3">
              {activePrompts.map((prompt) => {
                const info = AGENT_INFO[prompt.agent_name] || { name: prompt.agent_name, description: '', color: 'slate' };
                const isExpanded = expandedAgent === prompt.agent_name;
                const versions = agentVersions[prompt.agent_name] || [];

                return (
                  <div
                    key={prompt.id}
                    className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden"
                  >
                    {/* Agent Header */}
                    <div className="bg-slate-50 dark:bg-slate-800/50 p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <button
                            onClick={() => loadAgentVersions(prompt.agent_name)}
                            className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded mr-2"
                          >
                            {loadingVersions === prompt.agent_name ? (
                              <div className="h-4 w-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                            ) : isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-slate-500" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-slate-500" />
                            )}
                          </button>
                          <div>
                            <h4 className="font-semibold text-slate-900 dark:text-white flex items-center">
                              <span className={`w-2 h-2 rounded-full bg-${info.color}-500 mr-2`} />
                              {info.name}
                              <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full">
                                v{prompt.version} active
                              </span>
                            </h4>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                              {info.description}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => setViewingPrompt(prompt)}
                            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
                            title="View prompt"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => startEditingPrompt(prompt)}
                            className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg"
                            title="Edit (create new version)"
                          >
                            <Edit3 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      {/* Last updated info */}
                      <div className="mt-2 flex items-center text-xs text-slate-400">
                        <Clock className="h-3 w-3 mr-1" />
                        Activated {prompt.activated_at ? new Date(prompt.activated_at).toLocaleDateString() : 'at creation'}
                        {prompt.notes && (
                          <span className="ml-3 italic">"{prompt.notes}"</span>
                        )}
                      </div>
                    </div>

                    {/* Version History */}
                    {isExpanded && (
                      <div className="border-t border-slate-200 dark:border-slate-700">
                        <div className="p-3 bg-slate-100/50 dark:bg-slate-900/50">
                          <h5 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center">
                            <History className="h-3 w-3 mr-1" /> Version History
                          </h5>
                        </div>
                        <div className="divide-y divide-slate-100 dark:divide-slate-800">
                          {versions.map((v) => (
                            <div
                              key={v.id}
                              className={`p-3 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800/50 ${
                                v.is_active ? 'bg-green-50/50 dark:bg-green-900/10' : ''
                              }`}
                            >
                              <div className="flex items-center">
                                <span className={`font-mono text-sm font-bold mr-3 ${
                                  v.is_active ? 'text-green-600 dark:text-green-400' : 'text-slate-400'
                                }`}>
                                  v{v.version}
                                </span>
                                <div>
                                  <div className="text-sm text-slate-700 dark:text-slate-300">
                                    {v.notes || 'No notes'}
                                    {v.is_active && (
                                      <span className="ml-2 text-xs bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 px-1.5 py-0.5 rounded">
                                        ACTIVE
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-xs text-slate-400">
                                    Created {new Date(v.created_at).toLocaleString()}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => setViewingPrompt(v)}
                                  className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
                                  title="View"
                                >
                                  <Eye className="h-3.5 w-3.5" />
                                </button>
                                {!v.is_active && (
                                  <button
                                    onClick={() => handleActivatePrompt(v.id, v.agent_name)}
                                    className="p-1.5 text-slate-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                                    title="Activate this version"
                                  >
                                    <RotateCcw className="h-3.5 w-3.5" />
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {activePrompts.length === 0 && (
                <div className="text-center py-10 text-slate-400">
                  <Sparkles className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No prompts found. Run the database migration to seed initial prompts.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* View Prompt Modal */}
      {viewingPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-xl w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white">
                  {AGENT_INFO[viewingPrompt.agent_name]?.name || viewingPrompt.agent_name}
                </h3>
                <p className="text-xs text-slate-500">
                  Version {viewingPrompt.version} {viewingPrompt.is_active && '(Active)'}
                </p>
              </div>
              <button
                onClick={() => setViewingPrompt(null)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <pre className="text-sm font-mono whitespace-pre-wrap text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-800 p-4 rounded-lg">
                {viewingPrompt.prompt_text}
              </pre>
            </div>
            <div className="p-4 border-t border-slate-200 dark:border-slate-800 flex justify-between items-center">
              <div className="text-xs text-slate-400">
                {viewingPrompt.notes && <span className="italic">"{viewingPrompt.notes}"</span>}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(viewingPrompt.prompt_text);
                  }}
                  className="px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"
                >
                  <Copy className="h-4 w-4 inline mr-1" /> Copy
                </button>
                <button
                  onClick={() => {
                    startEditingPrompt(viewingPrompt);
                    setViewingPrompt(null);
                  }}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Edit3 className="h-4 w-4 inline mr-1" /> Edit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
